// File: frontend/src/components/visualization/ObsidianGraph.js
// Obsidian-style force-directed graph visualization using D3.js

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import {
  ZoomIn, ZoomOut, Maximize2, Users,
  Circle, X, Info, Link as LinkIcon
} from 'lucide-react';
import { DEFAULT_EDGE_LAYERS, isTypeVisible } from '../../utils/edgeLayers';

const ObsidianGraph = ({
  people = [],
  selectedPersonId = null,
  edgeLayers = DEFAULT_EDGE_LAYERS,
  soloLayer = null,
  transactionEdges = [],
  onNodeClick = null,
  onUpdateConnection = null,
  onDeleteConnection = null,
  layoutType = 'force'
}) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [connectionMode, setConnectionMode] = useState(false);
  const [sourceNode, setSourceNode] = useState(null);
  const [stats, setStats] = useState({ nodes: 0, edges: 0, clusters: 0 });
  const [nodeDistanceMultiplier, setNodeDistanceMultiplier] = useState(1); // Multiplier for node distance

  // Theme colors (Obsidian-inspired dark theme)
  const theme = {
    background: '#1e1e1e',
    node: {
      default: '#4c51bf',
      person: '#667eea',
      business: '#48bb78',
      crypto_wallet: '#a855f7',
      selected: '#f6ad55',
      hover: '#fc8181',
      text: '#ffffff'
    },
    edge: {
      family: '#10b981',
      friend: '#3b82f6',
      enemy: '#ef4444',
      associate: '#6b7280',
      employer: '#8b5cf6',
      employee: '#a78bfa',
      suspect: '#ef4444',
      witness: '#f59e0b',
      victim: '#ec4899',
      owner: '#48bb78',
      owns_business: '#38a169',
      board_member: '#7c3aed',
      transaction: '#0891b2',
      other: '#6b7280'
    },
    grid: '#2d3748',
    text: '#e2e8f0'
  };

  // Prepare graph data
  const prepareGraphData = useCallback(() => {
    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    // Create nodes
    people.forEach(person => {
      const nodeId = person.type === 'business' ? person.id : person.id.toString();
      const node = {
        id: nodeId,
        name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || t('relationshipDiagram.unknown'),
        category: person.category || t('relationshipDiagram.unknown'),
        status: person.status,
        caseName: person.case_name,
        type: person.type || 'person',
        connections: person.connections || [],
        data: person
      };

      nodes.push(node);
      nodeMap.set(nodeId, node);
    });

    // Create edges from connections
    people.forEach(person => {
      const sourceId = person.type === 'business' ? person.id : person.id.toString();

      if (person.connections && Array.isArray(person.connections)) {
        person.connections.forEach(conn => {
          // Bad data (e.g. person_id: null from an old bug or an API write)
          // must be skipped, not crash the whole graph (issue #56)
          if (!conn || conn.person_id == null) return;
          // Relationship layers (issue #65, replacing the three edge classes
          // from #50). A soloed layer hides everything else outright.
          if (!isTypeVisible(conn.type || 'other', edgeLayers, soloLayer)) return;
          const targetId = String(conn.person_id);

          // Only add edge if target exists
          if (nodeMap.has(targetId)) {
            // `_reverse` edges are stored on the child pointing at its parent
            // (a business records who owns it), but hierarchical layout ranks
            // the edge SOURCE as the parent — so flip them to keep the owner on
            // top of the chain (issue #65).
            links.push({
              source: conn._reverse ? targetId : sourceId,
              target: conn._reverse ? sourceId : targetId,
              type: conn.type || 'other',
              note: conn.note || '',
              strength: getConnectionStrength(conn.type)
            });
          }
        });
      }
    });

    // Transaction-derived edges (issue #50), pre-aggregated per party pair
    if (isTypeVisible('transaction', edgeLayers, soloLayer)) {
      transactionEdges.forEach(edge => {
        if (nodeMap.has(String(edge.source)) && nodeMap.has(String(edge.target))) {
          links.push({
            source: String(edge.source),
            target: String(edge.target),
            type: 'transaction',
            note: edge.note || '',
            strength: 0.9
          });
        }
      });
    }

    return { nodes, links };
  }, [people, edgeLayers, soloLayer, transactionEdges, t]);

  // Get connection strength for force simulation
  const getConnectionStrength = (type) => {
    const strengths = {
      family: 1.5,
      friend: 1.2,
      enemy: 0.5,
      associate: 1.0,
      employer: 1.3,
      employee: 1.3,
      board_member: 1.4,
      owns_business: 1.4,
      suspect: 1.4,
      witness: 1.1,
      victim: 1.0,
      other: 0.8
    };
    return strengths[type] || 1.0;
  };

  // Get node size based on connections
  const getNodeSize = (node) => {
    const baseSize = 8;
    const connectionCount = node.connections?.length || 0;
    return baseSize + Math.min(connectionCount * 2, 20);
  };

  // Get node color (without hover - handled by D3 directly)
  const getNodeColor = (node, isSelected) => {
    if (isSelected) return theme.node.selected;
    if (node.type === 'business') return theme.node.business;
    if (node.type === 'crypto_wallet') return theme.node.crypto_wallet;

    // Color by category
    const categoryColors = {
      'Person of Interest': '#ef4444',
      'Client': '#10b981',
      'Witness': '#f59e0b',
      'Victim': '#ec4899',
      'Suspect': '#dc2626',
      'Business': theme.node.business
    };

    return categoryColors[node.category] || theme.node.person;
  };

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Initialize and update graph
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return;

    const { nodes, links } = prepareGraphData();

    // Update stats
    setStats({
      nodes: nodes.length,
      edges: links.length,
      clusters: new Set(nodes.map(n => n.category)).size
    });

    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);
    const width = dimensions.width;
    const height = dimensions.height;

    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Main group for zoom/pan
    const g = svg.append('g');

    // Create force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .distance(d => (100 / d.strength) * nodeDistanceMultiplier) // Apply multiplier here
        .strength(d => d.strength * 0.3)
      )
      .force('charge', d3.forceManyBody()
        .strength(-300)
        .distanceMax(400)
      )
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => getNodeSize(d) + 10))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY(height / 2).strength(0.05));

    // Create arrow markers for directed edges
    const defs = svg.append('defs');

    Object.keys(theme.edge).forEach(type => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 20)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', theme.edge[type]);
    });

    // Create edges
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => theme.edge[d.type] || theme.edge.other)
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', d => `url(#arrow-${d.type})`)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke-width', 4)
          .attr('stroke-opacity', 1);

        // Show edge tooltip
        g.append('text')
          .attr('class', 'edge-tooltip')
          .attr('x', (d.source.x + d.target.x) / 2)
          .attr('y', (d.source.y + d.target.y) / 2)
          .attr('text-anchor', 'middle')
          .attr('fill', theme.text)
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .style('pointer-events', 'none')
          .text(d.type === 'transaction' && d.note ? d.note : t(`obsidianGraph.edgeTypes.${d.type}`));
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('stroke-opacity', 0.6);
        g.selectAll('.edge-tooltip').remove();
      });

    // Create node groups
    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .call(d3.drag()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded))
      .on('click', handleNodeClick)
      .on('contextmenu', handleContextMenu);

    // Add circles for nodes
    node.append('circle')
      .attr('r', d => getNodeSize(d))
      .attr('fill', d => getNodeColor(d, selectedNode?.id === d.id))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        // Handle hover with D3 directly - no React state
        d3.select(this)
          .transition()
          .duration(200)
          .attr('stroke-width', 4)
          .attr('r', getNodeSize(d) + 3);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('stroke-width', 2)
          .attr('r', getNodeSize(d));
      });

    // Add icons for node types
    node.each(function(d) {
      const nodeGroup = d3.select(this);
      const size = getNodeSize(d);

      // Add text label
      nodeGroup.append('text')
        .attr('dy', size + 15)
        .attr('text-anchor', 'middle')
        .attr('fill', theme.text)
        .attr('font-size', '11px')
        .attr('font-weight', '500')
        .style('pointer-events', 'none')
        .text(d.name.length > 20 ? d.name.substring(0, 18) + '...' : d.name);

      // Add connection count badge
      const connectionCount = d.connections?.length || 0;
      if (connectionCount > 0) {
        nodeGroup.append('circle')
          .attr('cx', size - 2)
          .attr('cy', -size + 2)
          .attr('r', 8)
          .attr('fill', '#3b82f6')
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);

        nodeGroup.append('text')
          .attr('x', size - 2)
          .attr('y', -size + 2)
          .attr('dy', '0.35em')
          .attr('text-anchor', 'middle')
          .attr('fill', '#fff')
          .attr('font-size', '9px')
          .attr('font-weight', 'bold')
          .style('pointer-events', 'none')
          .text(connectionCount);
      }
    });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragStarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragEnded(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    function handleNodeClick(event, d) {
      event.stopPropagation();
      setSelectedNode(d);
      setShowNodeDetails(true);
      if (onNodeClick) onNodeClick(d.data);

      if (connectionMode) {
        if (!sourceNode) {
          setSourceNode(d);
        } else if (sourceNode.id !== d.id) {
          // Create connection
          handleCreateConnection(sourceNode, d);
          setConnectionMode(false);
          setSourceNode(null);
        }
      }
    }

    function handleContextMenu(event, d) {
      event.preventDefault();
      event.stopPropagation();

      setContextMenu({
        x: event.pageX,
        y: event.pageY,
        node: d
      });
    }

    // Center on selected person or initialize centered view
    if (selectedPersonId) {
      // Wait for simulation to stabilize before centering on selected node
      setTimeout(() => {
        const selectedNodeData = nodes.find(n => n.id === selectedPersonId.toString());
        if (selectedNodeData && selectedNodeData.x && selectedNodeData.y) {
          const transform = d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(1.5)
            .translate(-selectedNodeData.x, -selectedNodeData.y);

          svg.transition()
            .duration(750)
            .call(zoom.transform, transform);
        }
      }, 500);
    } else {
      // Initialize with centered view for general graph
      setTimeout(() => {
        const transform = d3.zoomIdentity
          .translate(width / 2, height / 2)
          .scale(1)
          .translate(-width / 2, -height / 2);

        svg.call(zoom.transform, transform);
      }, 100);
    }

    // Store simulation reference
    simulationRef.current = simulation;

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [dimensions, people, selectedPersonId, selectedNode, connectionMode, sourceNode, onNodeClick, prepareGraphData, t]);

  // Handle zoom controls
  const handleZoom = (direction) => {
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom();

    svg.transition()
      .duration(350)
      .call(zoom.scaleBy, direction === 'in' ? 1.3 : 0.7);
  };

  // Handle fit to view
  const handleFitView = () => {
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom();

    svg.transition()
      .duration(750)
      .call(zoom.transform, d3.zoomIdentity);
  };

  // Handle create connection
  const handleCreateConnection = (source, target) => {
    // Person↔business links aren't supported by the connections model yet —
    // parseInt('business-N') is NaN and used to persist person_id: null,
    // permanently crashing this view (issue #56). Wallet edges are read-only
    // here too, but for a different reason: they're written through the
    // relationships API from the wallet detail modal (issue #82), not the
    // person.connections JSONB model this in-graph creation flow calls.
    if (source.type === 'business' || target.type === 'business') {
      alert(t('obsidianGraph.errorBusinessConnectionsNotSupported'));
      return;
    }
    if (source.type === 'crypto_wallet' || target.type === 'crypto_wallet') {
      alert(t('obsidianGraph.errorWalletConnectionsNotSupported'));
      return;
    }
    const sourceId = parseInt(source.id, 10);
    const targetId = parseInt(target.id, 10);
    if (!Number.isInteger(sourceId) || !Number.isInteger(targetId)) {
      alert(t('obsidianGraph.errorUnrecognisedNode'));
      return;
    }
    // This would open a modal to select connection type
    const type = prompt(t('obsidianGraph.promptConnectionType'));
    if (type && onUpdateConnection) {
      onUpdateConnection(sourceId, targetId, type, '');
    }
  };

  return (
    <div ref={containerRef} className="relative w-full h-full bg-gray-900">
      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{ background: theme.background }}
        onClick={() => {
          setSelectedNode(null);
          setShowNodeDetails(false);
          setContextMenu(null);
        }}
      />

      {/* Controls */}
      <div className="absolute top-4 end-4 flex flex-col space-y-2 z-10">
        <button
          onClick={() => handleZoom('in')}
          className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-lg transition"
          title={t('obsidianGraph.zoomIn')}
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleZoom('out')}
          className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-lg transition"
          title={t('obsidianGraph.zoomOut')}
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={handleFitView}
          className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-lg transition"
          title={t('obsidianGraph.fitToView')}
        >
          <Maximize2 className="w-5 h-5" />
        </button>
        <button
          onClick={() => setConnectionMode(!connectionMode)}
          className={`p-2 ${connectionMode ? 'bg-blue-600' : 'bg-gray-800'} hover:bg-blue-700 text-white rounded-lg shadow-lg transition`}
          title={t('relationshipDiagram.addConnection')}
        >
          <LinkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Panel */}
      <div className="absolute top-4 start-4 bg-gray-800 bg-opacity-90 text-white rounded-lg shadow-lg p-3 z-10">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-1">
            <Users className="w-4 h-4 text-blue-400" />
            <span>{t('obsidianGraph.nodesCountShort', { count: stats.nodes })}</span>
          </div>
          <div className="flex items-center space-x-1">
            <LinkIcon className="w-4 h-4 text-green-400" />
            <span>{t('obsidianGraph.edgesCountShort', { count: stats.edges })}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Circle className="w-4 h-4 text-purple-400" />
            <span>{t('obsidianGraph.typesCountShort', { count: stats.clusters })}</span>
          </div>
        </div>
      </div>

      {/* Connection Mode Banner */}
      {connectionMode && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-10">
          <div className="flex items-center space-x-2">
            <LinkIcon className="w-5 h-5" />
            <span className="font-medium">
              {sourceNode ? t('obsidianGraph.clickTargetNode', { name: sourceNode.name }) : t('obsidianGraph.clickSourceNode')}
            </span>
            <button
              onClick={() => {
                setConnectionMode(false);
                setSourceNode(null);
              }}
              className="ms-4 p-1 hover:bg-blue-700 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Node Details Panel */}
      {showNodeDetails && selectedNode && (
        <div className="absolute top-20 end-4 bg-gray-800 bg-opacity-95 text-white rounded-lg shadow-xl p-4 w-64 z-20">
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-bold text-lg">{selectedNode.name}</h3>
            <button
              onClick={() => setShowNodeDetails(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-400">{t('obsidianGraph.typeLabel')}</span>
              <span className="ms-2 font-medium">{selectedNode.type === 'business' ? t('obsidianGraph.businessLabel') : t('obsidianGraph.personLabel')}</span>
            </div>

            <div>
              <span className="text-gray-400">{t('obsidianGraph.categoryLabel')}</span>
              <span className="ms-2 font-medium">{selectedNode.category}</span>
            </div>

            {selectedNode.status && (
              <div>
                <span className="text-gray-400">{t('obsidianGraph.statusLabel')}</span>
                <span className="ms-2 font-medium">{selectedNode.status}</span>
              </div>
            )}

            {selectedNode.caseName && (
              <div>
                <span className="text-gray-400">{t('obsidianGraph.caseLabel')}</span>
                <span className="ms-2 font-medium">{selectedNode.caseName}</span>
              </div>
            )}

            <div>
              <span className="text-gray-400">{t('obsidianGraph.connectionsLabel')}</span>
              <span className="ms-2 font-medium">{selectedNode.connections?.length || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="absolute bg-gray-800 text-white rounded-lg shadow-xl py-1 z-30 min-w-[160px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            onClick={() => {
              if (onNodeClick) onNodeClick(contextMenu.node.data);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 hover:bg-gray-700 flex items-center space-x-2 text-start"
          >
            <Info className="w-4 h-4" />
            <span>{t('peopleList.viewDetails')}</span>
          </button>

          <button
            onClick={() => {
              setSourceNode(contextMenu.node);
              setConnectionMode(true);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 hover:bg-gray-700 flex items-center space-x-2 text-start"
          >
            <LinkIcon className="w-4 h-4" />
            <span>{t('relationshipDiagram.addConnection')}</span>
          </button>

          <div className="border-t border-gray-700 my-1"></div>

          <button
            onClick={() => setContextMenu(null)}
            className="w-full px-4 py-2 hover:bg-gray-700 flex items-center space-x-2 text-start text-gray-400"
          >
            <X className="w-4 h-4" />
            <span>{t('common.close')}</span>
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 start-4 bg-gray-800 bg-opacity-90 text-white rounded-lg shadow-lg p-3 z-10 max-w-xs">
        <h4 className="font-bold text-sm mb-2">{t('relationshipDiagram.connectionTypesTitle')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {Object.entries(theme.edge).map(([type, color]) => (
            <div key={type} className="flex items-center space-x-2">
              <div className="w-6 h-0.5" style={{ backgroundColor: color }}></div>
              <span>{t(`obsidianGraph.edgeTypes.${type}`)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Node Distance Control */}
      <div className="absolute bottom-20 end-4 bg-gray-800 bg-opacity-90 text-white rounded-lg shadow-lg p-3 z-10">
        <label htmlFor="distance-slider" className="block text-sm font-medium mb-2">{t('obsidianGraph.nodeDistanceMultiplier')}</label>
        <input
          id="distance-slider"
          type="range"
          min="1"
          max="3"
          step="0.1"
          value={nodeDistanceMultiplier}
          onChange={(e) => {
            const newMultiplier = parseFloat(e.target.value);
            setNodeDistanceMultiplier(newMultiplier);

            // Restart simulation with updated multiplier
            if (simulationRef.current) {
              simulationRef.current.force('link').distance(d => (100 / d.strength) * newMultiplier);
              simulationRef.current.alpha(1).restart();
            }
          }}
          className="w-full"
        />
        <div className="text-xs mt-1">{t('obsidianGraph.multiplierValue', { value: nodeDistanceMultiplier.toFixed(1) })}</div>
      </div>
    </div>
  );
};

export default ObsidianGraph;
